class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.closeSearchIcon = this.querySelector('.close-search-results_button');
    this.suggestionWrapper = this.querySelector('.search-recommended-products__wrapper');
    this.mobileSearchTrigger = document.querySelectorAll('.mobile-search__tigger');

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.closeSearchIcon?.addEventListener('click', () => this.close());
    this.mobileSearchTrigger?.forEach((each) => {
      each.addEventListener('click', this.onFocus.bind(this));
    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    this.noSearchResultsWrapper = this.querySelector('.search-no-results__wrapper');
    super.onChange();
    const newSearchTerm = this.getQuery();
    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;
    if (!this.searchTerm.length) {
      const newSearchTerm = this.getQuery();
      if ((!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) && newSearchTerm == '') {
        // Remove the results when they are no longer relevant for the new search term
        this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
      }

      this.suggestionWrapper?.classList.remove('hidden');
      this.noSearchResultsWrapper?.classList.add('hidden');
      return;
    }

    if (newSearchTerm != '') {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  //highlight Search Results title
  highlightResults(query) {
    if (!query) return;
    const parts = query.trim().split(/\s+/);
    const regex = new RegExp(`(${parts.join('|')})`, 'gi');
    const items = document.querySelectorAll('.predictive-search__item-heading');
    items.forEach(item => {
      const original = item.textContent;
      item.innerHTML = original.replace(regex, '<b class="highlighted">$1</b>');
    });
  }

  checkAndHideSearchResults() {
    if (this.searchTerm.length != 0) {
      this.suggestionWrapper?.classList.add('hidden');
      this.predictiveSearchResults?.classList.remove('hidden');
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    this.open();
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    this.noSearchResultsWrapper = this.querySelector('.search-no-results__wrapper');
    if (!this.getQuery().length) setTimeout(() => {this.suggestionWrapper?.classList.remove('hidden');this.noSearchResultsWrapper?.classList.add('hidden')}, 200); 
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      this.checkAndHideSearchResults();
      this.highlightResults(searchTerm); 
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&resources[type]=product&resources[limit]=10&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })

      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
        this.checkAndHideSearchResults();
        this.highlightResults(searchTerm); 

      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    const newSearchTerm = this.getQuery();
    if ((!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) && newSearchTerm == '') {
      // Remove the results when they are no longer relevant for the new search term
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }    
    if (newSearchTerm != '') {
      this.predictiveSearchResults.innerHTML = resultsMarkup;
    }
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    
    const productItemsList = Array.from(document.querySelectorAll('#predictive-search-results-products-list .predictive-search__list-item'));
    this.filterCategoriesAndPagination(productItemsList);
    this.open();
  }

/**search filter **/
filterCategoriesAndPagination(productItemsList) {
  const searchCategories = document.querySelectorAll('.search-category');
  const clearFilterButton = document.getElementById('clear-Filter');
  const wrapper = document.getElementById('search-collection-wrapper');
  const productCountElement = document.querySelector('.product_count_number');
  const productItemsArray = Array.from(productItemsList);
  const resultsList = $('#predictive-search-results-products-list');
  const paginationContainer = $('#pagination-container');
  const articleLinks = document.querySelectorAll('.article_serach_list a');
  const articleHeading = document.querySelector('.article-search-heading');

  function simpleTemplating(data) {
    let html = '';
    data.forEach(item => {
      html += item.outerHTML;
    });
    return html;
  }


  // Hide article heading and all articles initially
  if (articleHeading) {
    articleHeading.style.display = 'none';
  }
  articleLinks.forEach(article => {
    article.style.display = 'none';
  });

  searchCategories.forEach(category => {
    category.addEventListener('click', function () {
      const dataTag = this.getAttribute('data-tag');
      const wasActive = this.classList.contains('active');

      // Remove active class from all and handle toggle
      searchCategories.forEach(cat => cat.classList.remove('active'));
      if (wasActive) {
        clearFilterButton.click();
        return;
      }

      this.classList.add('active');
      clearFilterButton.classList.remove('hidden');

      const dataTagCount = this.getAttribute('data-tag-count');
      if (productCountElement) {
        productCountElement.textContent = dataTagCount;
      }

      let shownCount = 0;
      articleLinks.forEach(article => {
        if (article.getAttribute('data-article-tag') === dataTag) {
          if (shownCount < 5) {
            article.style.display = 'block';
            shownCount++;
          } else {
            article.style.display = 'none';
          }
        } else {
          article.style.display = 'none';
        }
      });

      if (articleHeading) {
        if (shownCount > 0) {
          articleHeading.style.display = 'block';
        } else {
          articleHeading.style.display = 'none';
        }
      }

      wrapper?.classList.add('clearfilter');

      const filteredItems = productItemsArray.filter(item =>
        item.getAttribute('data-meta-tag') === dataTag
      );

    });
  });

  // Clear filter button click
  clearFilterButton?.addEventListener('click', function (event) {
    event.preventDefault();
    searchCategories.forEach(cat => cat.classList.remove('active'));
    wrapper?.classList.remove('clearfilter');

    if (productCountElement) {
      productCountElement.textContent = productItemsArray.length;
    }

    articleLinks.forEach(article => {
      article.style.display = 'none';
    });

    if (articleHeading) {
      articleHeading.style.display = 'none';
    }

  });
}


  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
